import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ValueAccessorBase } from '@capturum/ui/api';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CapturumInputComponent } from '@capturum/ui/input';

@Component({
  selector: 'app-input-code',
  templateUrl: './input-code.component.html',
  styleUrls: ['./input-code.component.scss'],
  providers: [ValueAccessorBase.getProviderConfig(InputCodeComponent)],
})
export class InputCodeComponent extends ValueAccessorBase<string> implements OnInit, OnDestroy {
  @Input()
  public inputCodeLength = 6;
  @Output()
  public onInitialized = new EventEmitter<boolean>();
  @Output() public valuesFilled = new EventEmitter();
  @ViewChildren(CapturumInputComponent, { read: ElementRef })
  public inputs: QueryList<ElementRef>;

  public inputsForm: UntypedFormGroup;
  private prevValues = [];
  private subs: Subscription = new Subscription();
  private isPasting = false;

  constructor(private formBuilder: UntypedFormBuilder) {
    super();
  }

  @HostListener('window:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.handleEnterKeyPress(event);
    } else if (event.key === 'Backspace') {
      this.handleBackspaceKeyPress(event);
    }
  }

  @HostListener('paste', ['$event'])
  public onPaste(event: ClipboardEvent): void {
    this.handlePasteEvent(event);
  }

  public ngOnInit(): void {
    this.initializeForm();
    this.setupValueChangesSubscription();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public writeValue(value: string): void {
    if (value) {
      super.writeValue(value);
    } else {
      this.inputsArray.reset();
    }
  }

  public focusOnNextInput(index: number, event: InputEvent, value: string): void {
    event.stopImmediatePropagation();
    if (value.trim() !== '') {
      this.prevValues[index] = value;
      this.ensureSingleCharacterInInput(index);
      this.moveToNextInputIfAvailable(index);
    }
  }

  public focusOnInput(index: number): void {
    this.inputs
      .toArray()
      [index].nativeElement.querySelector('input')
      .focus();
  }

  // @ts-ignore
  public get inputsArray(): UntypedFormArray {
    return this.inputsForm.get('myCode') as UntypedFormArray;
  }

  public focus(): void {
    this.inputs
      .toArray()
      [0].nativeElement.querySelector('input')
      .focus();
  }

  private initializeForm(): void {
    this.inputsForm = this.formBuilder.group({
      myCode: this.formBuilder.array([]),
    });

    for (let i = 0; i < this.inputCodeLength; i++) {
      this.inputsArray.push(new UntypedFormControl(null, [Validators.required]));
    }
  }

  private setupValueChangesSubscription(): void {
    this.subs = this.inputsArray.valueChanges
      .pipe(
        debounceTime(100),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.updateComponentValue();
        this.emitValuesFilledIfValid();
      });
  }

  private updateComponentValue(): void {
    this.value = this.inputsArray.value.join('');
  }

  private emitValuesFilledIfValid(): void {
    if (this.inputsArray.valid && !this.isPasting) {
      this.valuesFilled.emit();
    }
  }

  private handleEnterKeyPress(event: KeyboardEvent): void {
    if (this.isAnyInputFieldFocused()) {
      event.preventDefault();
      if (this.inputsArray.valid) {
        this.valuesFilled.emit();
      }
    }
  }

  private isAnyInputFieldFocused(): boolean {
    return this.inputs.some(input => 
      input.nativeElement.querySelector('input') === document.activeElement
    );
  }

  private handleBackspaceKeyPress(event: KeyboardEvent): void {
    if (!this.isInputCodeFieldFocused()) {
      return;
    }

    const currentIndex = this.getCurrentFocusedInputIndex();
    if (currentIndex === -1) {
      return;
    }

    const currentValue = this.inputsArray.controls[currentIndex].value;
    
    // If current field has a value, clear it and stay on the same field
    if (currentValue) {
      this.inputsArray.controls[currentIndex].setValue('');
      this.prevValues[currentIndex] = '';
      event.preventDefault();
    }
    // If current field is empty and not the first field, move to previous field and clear it
    else if (currentIndex > 0) {
      this.moveToPreviousFieldAndClearIt(currentIndex);
      event.preventDefault();
    }
  }

  private isInputCodeFieldFocused(): boolean {
    return document.activeElement.className.includes('input-code');
  }

  private getCurrentFocusedInputIndex(): number {
    const inputElements = this.inputs.toArray();
    const activeElementId = document.activeElement.parentElement.parentElement.parentElement.getAttribute('id');
    return inputElements.findIndex((inputElement, index) => 
      activeElementId === `input-code-${index}`
    );
  }



  private moveToPreviousFieldAndClearIt(currentIndex: number): void {
    const previousIndex = currentIndex - 1;
    this.inputsArray.controls[previousIndex].setValue('');
    this.prevValues[previousIndex] = '';
    this.focusOnInput(previousIndex);
  }

  private handlePasteEvent(event: ClipboardEvent): void {
    event.preventDefault();
    this.isPasting = true;
    
    const pastedData = event.clipboardData?.getData('text/plain');
    if (this.isValidPasteData(pastedData)) {
      this.processPastedData(pastedData);
    }
    
    this.resetPastingFlagAfterDelay();
  }

  private isValidPasteData(pastedData: string | null): boolean {
    return pastedData && pastedData.length > 0;
  }

  private processPastedData(pastedData: string): void {
    const cleanData = this.cleanPastedData(pastedData);
    if (cleanData.length > 0) {
      this.distributePastedCodeAcrossFields(cleanData);
      this.focusOnLastFilledInput(cleanData);
    }
  }

  private cleanPastedData(pastedData: string): string {
    return pastedData.replace(/\D/g, '').slice(0, this.inputCodeLength);
  }

  private distributePastedCodeAcrossFields(cleanData: string): void {
    for (let i = 0; i < this.inputCodeLength; i++) {
      const value = cleanData[i] || '';
      this.inputsArray.controls[i].setValue(value);
      this.prevValues[i] = value;
    }
  }

  private focusOnLastFilledInput(cleanData: string): void {
    const lastFilledIndex = Math.min(cleanData.length - 1, this.inputCodeLength - 1);
    this.focusOnInput(lastFilledIndex);
  }

  private resetPastingFlagAfterDelay(): void {
    setTimeout(() => {
      this.isPasting = false;
    }, 100);
  }

  private ensureSingleCharacterInInput(index: number): void {
    const currentInputElement = this.inputs
      .toArray()
      [index].nativeElement.querySelector('input');

    if (currentInputElement.value?.length > 1) {
      currentInputElement.value = currentInputElement.value[0];
    }
  }

  private moveToNextInputIfAvailable(index: number): void {
    if (this.inputs.toArray()[index + 1]) {
      this.focusOnInput(index + 1);
    }
  }
}
